import { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import _ from 'lodash';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { BiHotel } from 'react-icons/bi';
import { TiWaves } from 'react-icons/ti';
import { IoMdAirplane } from 'react-icons/io';
import { AiTwotoneCar } from 'react-icons/ai';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { TravelTypes, NoHotelImgSrc, NoCarImgSrc } from '../../../constants';
import FlightItinerary from '../../TravelOption/FlightInformation/FlightItinerary';
import CarItinerary from '../../TravelOption/CarsInformation/CarItinerary';
import HotelItinerary from '../../TravelOption/HotelInformation/HotelItinerary';
import CruiseItinerary from '../../TravelOption/CruiseInformation/CruiseItinerary';
import Button from '../../Button';
import TourItineraryContent from '../../TravelOption/TourInformation/TourItinerary/TourItineraryContent';
import pluralize from '../../../utils/pluralize';
import InvalidFlightTooltip from '../../ToolTip/InvalidFlightToolTip';

const mapCruiseInfoProps = (cruiseLine, ship) => {
  const arr = [];
  if (cruiseLine && cruiseLine.cruiseLineName) arr.push({ name: 'Cruise line', value: cruiseLine.cruiseLineName });
  if (ship && ship.shipName) arr.push({ name: 'Ship', value: ship.shipName });
  if (ship && ship.shipYearBuilt) arr.push({ name: 'Built', value: ship.shipYearBuilt });
  if (ship && ship.shipYearRefurbished) arr.push({ name: 'Refurbished', value: ship.shipYearRefurbished });
  if (ship && ship.shipCapacity) arr.push({ name: 'Capacity', value: ship.shipCapacity });

  return arr;
};

const Trip = ({ trip, doShowPrice }) => {
  const { travelAgency } = useSelector((store) => store.user);
  const [doShowViewOption, setDoShowViewOption] = useState(false);
  const { longTripTitle, tripSegments, roomSearchInput } = trip;
  const brochureSettings = travelAgency?.settings?.brochureSettings || {};
  const doHideItineraryOverview = !!brochureSettings?.doHideItineraryOverview;
  const {
    adults, children = [], seniors, lapInfants, seatInfants,
  } = (roomSearchInput && roomSearchInput[0]) || {};

  const iconComponents = {
    cruise: TiWaves,
    flight: IoMdAirplane,
    hotel: BiHotel,
    carrental: AiTwotoneCar,
    tour: TiWaves,
  };

  let flightPassengerMessage = '';
  let hotelPassengerMessage = '';
  let rentalCarPassengerMessage = '';

  const tripTypes = tripSegments.map((tripSegment) => {
    const type = Object.keys(TravelTypes).find(
      (travelType) => TravelTypes[travelType] === tripSegment.itinerary.mode,
    );
    return { ...tripSegment, travelType: type };
  });

  const tripsByType = tripTypes.reduce((acc, el) => {
    acc[el.travelType] = acc[el.travelType] ? [...acc[el.travelType], el] : [el];
    return acc;
  }, {});

  const tripTypesUnique = _.uniqBy(tripTypes, ({ travelType }) => travelType);

  const cruise = tripTypesUnique.find(
    (el) => el.itinerary.mode === TravelTypes.cruise,
  );
  const flight = tripTypesUnique.find(
    (el) => el.itinerary.mode === TravelTypes.flight,
  );
  const hasInvalidFlight = tripSegments.some(
    (segment) => segment.itinerary.isIncompleteItinerary,
  );
  const hotel = tripTypesUnique.find(
    (el) => el.itinerary.mode === TravelTypes.hotel,
  );
  const carrental = tripTypesUnique.find(
    (el) => el.itinerary.mode === TravelTypes.carrental,
  );
  if (tripTypesUnique.length > 1) {
    if (flight) flightPassengerMessage = 'Flight: ';
    if (hotel) hotelPassengerMessage = 'Hotel: ';
    if (carrental) rentalCarPassengerMessage = 'Rental car: ';
  }
  if (flight) {
    flightPassengerMessage += `${pluralize(adults, 'adult', 'adults')} ${pluralize(seniors, 'senior', 'seniors')} ${pluralize(children.length, 'child', 'children')} ${pluralize(lapInfants, 'lap infant', 'lap infants')} ${pluralize(seatInfants, 'Seat Infant', 'Seat Infants')}`.replace(/,\s*$/, '');
  }
  if (hotel || carrental) {
    const numberOfAdultsAndSeniors = adults + seniors;
    if (hotel) {
      hotelPassengerMessage += `${pluralize(numberOfAdultsAndSeniors, 'guest', 'guests')}`.replace(/,$/, '');
    }
    if (carrental) {
      rentalCarPassengerMessage += `${pluralize(numberOfAdultsAndSeniors, 'passenger', 'passengers')}`.replace(/,$/, '');
    }
  }

  // Sort tripTypes to show cruise logo on top
  const tripTypesSorted = _.orderBy(tripTypesUnique, ({ travelType }) => travelType, cruise && tripTypesUnique.length > 2 ? 'desc' : 'asc');

  const renderInfoCard = () => {
    if (cruise) {
      const { ship } = cruise.itinerary.itineraryDetail;
      const { cruiseLine } = cruise.itinerary.itineraryDetail;
      const mappedCruiseProps = mapCruiseInfoProps(cruiseLine, ship);

      return mappedCruiseProps.map((v, i) => (
        <div className="whitespace-no-wrap" key={v.name}>
          {`${v.name}: ${v.value} `}
          {i < mappedCruiseProps.length - 1 && <span>&bull;&nbsp;</span>}
        </div>
      ));
    }
    return (
      <div>
        {`${dayjs(tripTypesUnique[0].startDateTime).format(
          'MMM D, YYYY',
        )} - ${dayjs(tripTypesUnique[0].endDateTime).format('MMM D, YYYY')}`}
      </div>
    );
  };

  const renderItineraryImage = () => (
    <div className="mr-4 rounded flex flex-wrap" style={{ width: '11%', minWidth: '120px', minHeight: '120px' }}>
      {tripTypesSorted.map(({ id, itinerary }) => {
        const isFlight = itinerary.mode === TravelTypes.flight;
        const isCruise = itinerary.mode === TravelTypes.cruise;
        const isHotel = itinerary.mode === TravelTypes.hotel;
        const isCar = itinerary.mode === TravelTypes.carrental;
        const isTour = itinerary.mode === TravelTypes.tour;

        let imgSrc = '';
        let imgClass = 'w-full rounded-lg';

        if (isFlight) {
          imgSrc = itinerary.flightSegments[0].flights[0].carrierImage;
          imgClass = 'h-full rounded-lg inline';
        } else if (isCruise) {
          imgSrc = itinerary.itineraryDetail.cruiseLine.cruiseLineLogo;
        } else if (isHotel) {
          imgSrc = itinerary.hotel.image || NoHotelImgSrc;
          imgClass = 'w-full h-full rounded-lg';
        } else if (isCar) {
          imgSrc = itinerary.bookingProviderInfo.agencyLogo || NoCarImgSrc;
          imgClass = 'w-full h-full rounded-lg';
        } else if (isTour) {
          imgSrc = itinerary.image;
        }

        return (
          <div
            key={`${id}-${itinerary.mode}`}
            style={{
              width: tripTypesUnique.length > 2 ? '50%' : '100%',
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              height: tripTypesUnique.length === 1 ? '100%' : '50%',
            }}
          >
            <div className={clsx('w-full h-full rounded p-px text-center', { 'flex flex-wrap content-center items-center': !isFlight })}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={imgSrc} alt="IMG" className={imgClass} />
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderItineraryBadge = () => (
    <div className="flex flex-row flex-wrap gap-2">
      {tripTypesUnique.map(({ travelType, id }) => {
        const IconType = iconComponents[travelType];

        return (
          <div key={`${id}-${travelType}`} className="inline-block">
            <div className="sm:p-1 bg-theme bg-opacity-25 rounded-lg text-theme flex flex-row items-center">
              <IconType className="ml-2" />
              <span className="px-2 capitalize">
                {travelType === 'carrental' ? 'Rental car' : travelType}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderItineraryOverview = (className = '') => (
    <>
      <div className={`flex gap-4 ${className}`}>
        <div>
          {!doHideItineraryOverview && <div className="font-bold">{longTripTitle}</div>}
          <div className="text-sm text-gray-600 flex flex-wrap">
            {cruise ? `Cruise departure date: ${dayjs(cruise.startDateTime).format('MMM D, YYYY')}` : ''}
          </div>
          <div className="text-sm text-gray-600 flex flex-wrap">
            {renderInfoCard()}
          </div>
          {flightPassengerMessage && <div className="text-sm text-gray-600 flex flex-wrap">{flightPassengerMessage}</div>}
          {hotelPassengerMessage && <div className="text-sm text-gray-600 flex flex-wrap">{hotelPassengerMessage}</div>}
          {rentalCarPassengerMessage && <div className="text-sm text-gray-600 flex flex-wrap">{rentalCarPassengerMessage}</div>}
        </div>
        {flight && hasInvalidFlight && <InvalidFlightTooltip />}
      </div>
    </>
  );

  const renderItineraries = () => {
    if (!doShowViewOption && !doHideItineraryOverview) return <></>;
    const itineraryTypes = [
      { type: 'flight', component: FlightItinerary, label: 'Flight' },
      { type: 'carrental', component: CarItinerary, label: 'Rental Car' },
      {
        type: 'hotel', component: HotelItinerary, label: 'Hotel', extraProps: { doShowChatGpt: !!travelAgency?.settings?.doShowChatGPTOnBrochure },
      },
      { type: 'tour', component: TourItineraryContent, label: 'Tour' },
      { type: 'cruise', component: CruiseItinerary, label: 'Cruise' },
    ];

    return (
      <div>
        {itineraryTypes.map(({
          type, component: Component, label, extraProps,
        }) => {
          const items = tripsByType[type];
          if (!items) return <></>;
          const itineraryLabel = items.length > 1 ? `${label}s` : label;
          return (
            <div key={type}>
              {!doHideItineraryOverview && <p className="text-2xl font-bold">{itineraryLabel}</p>}
              {doHideItineraryOverview && renderItineraryOverview('border p-3 rounded-lg -mb-1')}
              {items.map(({ itinerary }) => (
                <div key={itinerary.id + (itinerary.flightKey || itinerary.hotelKey || itinerary.uniqueKey || (itinerary.itineraryDetail?.itineraryId || ''))}>
                  <Component
                    tripId={trip?.id}
                    itinerary={itinerary}
                    doShowPrice={doShowPrice}
                    tripRoomSearchInput={trip?.roomSearchInput || undefined}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...extraProps}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const renderItineraryHeader = () => {
    if (doHideItineraryOverview) return <></>;

    return (
      <div className={`flex sm:flex-row flex-col${doShowViewOption ? ' mb-4' : ''}`}>
        {renderItineraryImage()}
        <div className="flex flex-col justify-between flex-grow">
          {renderItineraryOverview()}
          <div className="sm:flex justify-between">
            {renderItineraryBadge()}
            <Button
              variant={Button.buttonVariants.LINK}
              onClick={() => setDoShowViewOption((prev) => !prev)}
              className="focus:outline-none"
            >
              <span>
                View This Option
                {doShowViewOption ? (
                  <FaChevronUp className="inline-block h-3" />
                ) : (
                  <FaChevronDown className="inline-block h-3" />
                )}
              </span>
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-2">
      {renderItineraryHeader()}
      {renderItineraries()}
    </div>
  );
};

Trip.propTypes = {
  trip: PropTypes.shape({
    id: PropTypes.string,
    passengerCity: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
    roomSearchInput: PropTypes.arrayOf(PropTypes.shape({
      adults: PropTypes.number,
      children: PropTypes.arrayOf(PropTypes.shape({})),
      seniors: PropTypes.number,
      lapInfants: PropTypes.number,
      seatInfants: PropTypes.number,
    })),
    tripSegments: PropTypes.arrayOf(PropTypes.shape({})),
    tripStartDate: PropTypes.string,
    longTripTitle: PropTypes.string,
  }).isRequired,
  doShowPrice: PropTypes.bool,
};

Trip.defaultProps = {
  doShowPrice: true,
};

export default Trip;
